import { CreateResponse, DeleteResponse, FieldError, PatchResponse } from '.';
import { JsonObject } from '../helpers';
import {
  getTypedIdentProvider,
  IdentProviderSubtype,
  IdentProviderType,
} from '../models/ident-provider';
import {
  CreateIdentProviderModel,
  IdentProviderModel,
} from '../models/ident-provider/ident-provider.model';
import { deleteApi, get, patch, post } from './sp-api.service';
import { AuthProvider } from './auth.service';
import { AxiosError } from 'axios';
import { SamlIdentConfig } from '../models/ident-provider/saml-ident-provider.model';
import { sortBy } from 'lodash';
import { OidcIdentConfig } from '../models/ident-provider/oidc-ident-provider.model';

export const MAX_PROVIDER_COUNT = 5;

export const getAllIdentProviders = async (): Promise<IdentProviderModel[]> => {
  const response = (await get(`/auth/identity/${AuthProvider.orgId}/providers/`)) as JsonObject[];

  if (!response?.length) {
    return [];
  }

  const providers = response.map((data) => new IdentProviderModel(data)).map(getTypedIdentProvider);

  return sortBy(providers, [
    ({ type }) => type !== 'magicLink',
    ({ name }) => String(name).toLowerCase(),
  ]);
};

export const getIdentProviderById = async (id: string): Promise<IdentProviderModel | null> => {
  const response = (await get(
    `/auth/identity/${AuthProvider.orgId}/providers/${id}`
  )) as JsonObject;
  return response ? new IdentProviderModel(response) : null;
};

export const createIdentProvider = async (
  provider: IdentProviderModel
): Promise<CreateResponse<IdentProviderModel>> => {
  const { name, type } = provider;

  const response = await post(`/auth/identity/${AuthProvider.orgId}/providers/`, {
    name,
    type,
  });

  const newModelId = response?.id as string;

  if (!response || !newModelId) {
    return createErrorHandler(response);
  }

  const newIdentProvider = new IdentProviderModel(response);

  return {
    created: true,
    model: getTypedIdentProvider(newIdentProvider),
  };
};

export const patchIdentProvider = async (
  id: string,
  data: Partial<CreateIdentProviderModel>,
  type: IdentProviderType,
  subtype?: IdentProviderSubtype
): Promise<PatchResponse> => {
  const { identityProviderConfig } = data;
  let patchData = { ...data };

  if (type === 'SAML') {
    switch (subtype) {
      case 'SAML-URL':
        {
          const { samlMetadataUrl } = identityProviderConfig as SamlIdentConfig;
          patchData = {
            ...patchData,
            identityProviderConfig: { samlMetadataUrl } as unknown as SamlIdentConfig,
          };
        }
        break;

      case 'SAML-XML':
        {
          const rawXml = (identityProviderConfig as SamlIdentConfig).samlMetadataXML;

          patchData = {
            ...patchData,
            identityProviderConfig: { samlMetadataXML: rawXml } as unknown as SamlIdentConfig,
          };
        }
        break;
      default:
        delete (patchData.identityProviderConfig as SamlIdentConfig)?.samlMetadataUrl;
        delete (patchData.identityProviderConfig as SamlIdentConfig)?.samlMetadataXML;
    }
  }

  if (type === 'OIDC') {
    const { clientSecret } = identityProviderConfig as OidcIdentConfig;
    if (!clientSecret) {
      delete (patchData.identityProviderConfig as OidcIdentConfig)?.clientSecret;
    }
  }

  return patch(
    `/auth/identity/${AuthProvider.orgId}/providers/${id}`,
    patchData,
    patchErrorHandler
  );
};

export const deleteIdentProvider = async (id: string): Promise<DeleteResponse> => {
  return deleteApi(`/auth/identity/${AuthProvider.orgId}/providers/${id}`);
};

const patchErrorHandler = (error: AxiosError): PatchResponse => {
  const response = error.response?.data as JsonObject;

  return {
    patched: false,
    error: response.status as string,
  };
};

const createErrorHandler = (response: JsonObject | null): CreateResponse<IdentProviderModel> => {
  return {
    created: false,
    fieldErrors: Array.isArray(response) ? (response as FieldError[]) : [],
  };
};
